import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
 import Autocomplete from '@mui/material/Autocomplete';
 import Checkbox from '@mui/material/Checkbox';
 import Grid from '@mui/material/Grid';
 import TextField from '@mui/material/TextField';
 import { AutocompleteInputChangeReason } from '@mui/material';

// MUI Icons
import AddIcon from '@mui/icons-material/Add';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

// Custom
import ModelTable from '@/ui/Table/ModelTable';
import dataController from '@/lib/dataController';
import DialogContext from '@/ui/DialogContext/DialogContext';
import IzletVodicSudionik from '@/models/izlet_vodic_sudionik';
import TableButtonDelete from '@/ui/Table/ActionButtons/TableButtonDelete';
import LoaderContext from '@/components/LoaderContext/LoaderContext';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';
import TableHeaderButton from '@/ui/Table/HeaderButtons/TableHeaderButton';
import InfoBox from '@/ui/InfoBox';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import { PickerItem } from '@/@types/controls/controls';
import { ConfirmResult } from '@/@types/lib/confirmDialog';

// This pane should only be shown in HPS izleti dialog
type IIzletSudioniciPage = {
  mode: string;
  izletId: number;
  onHasVodiciSubmitUvjet?: (value: boolean) => void;
  setDataChanged: React.Dispatch<React.SetStateAction<boolean>>;
};

const titleSudionici = 'titles.sudionici';
const titleVodici = 'titles.vodici';
const addLabel = 'buttons.add';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const IzletSudioniciPane = (props: IIzletSudioniciPage) => {
  const { t } = useTranslation();
  const dialogContext = useContext(DialogContext);

  const { mode, izletId, onHasVodiciSubmitUvjet, setDataChanged } = props;

  const [[sudioniciRecords, vodiciRecords], setRecords] = useState<[DCRecord[], DCRecord[]]>([[], []]);
  const [hpsUsersPickerRecords, setHpsUsersPickerRecords] = useState<PickerItem[]>([]);
  const [selectedSudionici, setSelectedSudionici] = useState<PickerItem[]>([]);
  const [selectedVodici, setSelectedVodici] = useState<PickerItem[]>([]);

  const [inputValueVodici, setInputValueVodici] = useState('');
  const [inputValueSudionici, setInputValueSudionici] = useState('');

  const loaderContext = useContext(LoaderContext);
  const snackbarContext = useContext(SnackbarContext);

  const allowCRUD = mode !== 'view';

  const dc = new dataController(IzletVodicSudionik, IzletVodicSudionik.apiPath.replace('{id}', izletId.toString()));

  const confirmDelete = (deletingRole: 'vodici' | 'sudionici'): Promise<ConfirmResult> => {
    const title = t('cdialogs.are_you_sure_delete_' + deletingRole);
    const text = '';
    const confirmButtonText = t('cdialogs.yes_delete');
    const cancelButtonText = t('cdialogs.no_cancel');

    return dialogContext.showConfirmDialog({ title, text, confirmButtonText, cancelButtonText });
  };

  const handleDelete = (evt: React.SyntheticEvent, sel: string[], role_id: number) => {
    const selIndexes: number[] = sel.map((id) => parseInt(id, 10));

    const deletingRole = role_id === 10 ? 'vodici' : 'sudionici';

    const records: DCRecord[] =
      deletingRole === 'vodici' ? selIndexes.map((index) => vodiciRecords[index]) : selIndexes.map((index) => sudioniciRecords[index]);

    const recordIds: number[] = records.map((rec) => rec.id).filter((id): id is number => id !== undefined && id !== null);

    if (recordIds.length === 0) {
      // how did u get here?
      return;
    }

    confirmDelete(deletingRole).then((result) => {
      if (!result.canceled && result.confirmed) {
        dc.DeleteRecords(recordIds, dc.apiPath, 'role_id=' + role_id).then((resp) => {
          if (resp.success) {
            snackbarContext.showNotification(t('notifications.delete'), 'success');
            setDataChanged(true);
            refreshRecords();
          } else {
            const msg = t('error.delete') + resp.error;
            snackbarContext.showNotification(msg, 'error');
          }
        });
      }
    });
  };

  useEffect(() => {
    refreshRecords();
    getPickerdata();
  }, []);

  useEffect(() => {
    if (onHasVodiciSubmitUvjet) {
      onHasVodiciSubmitUvjet(!!vodiciRecords.length);
    }
  }, [vodiciRecords]);

  const getPickerdata = () => {
    dc.GetData('hps/izleti-vodici-i-sudionici?need_for=picker')
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setHpsUsersPickerRecords(resp.data as PickerItem[]);
        }
      })
      .catch((resp) => {
        console.log(resp);
      });
  };

  const refreshRecords = () => {
    loaderContext.toggleLoading(true);
    dc.GetData()
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setRecords([resp.data.filter((rec) => rec.uloga === 20), resp.data.filter((rec) => rec.uloga === 10)]);
          if (resp.data.length === 0) {
            // snackbarContext.showNotification(t('messages.nodata'), 'warning');
          }
        }
      })
      .catch((resp) => {
        console.log(resp);
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  const handleAdd = (role_id: 10 | 20, rec_ids: number[]) => {
    loaderContext.toggleLoading(true);
    dc.InsertRecord({ user_ids: rec_ids, role_id: role_id })
      .then((result) => {
        if (result.success) {
          snackbarContext.showNotification(t('notifications.insert'), 'success');
          setDataChanged(true);
          refreshRecords();
          setSelectedSudionici([]);
          setSelectedVodici([]);
        } else if (result.error) {
          const msg = t('error.addRecord') + result.error;
          snackbarContext.showNotification(msg, 'error');
        }
      })
      .catch((resp) => {
        snackbarContext.showNotification(resp.error, 'error');
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  const handleChangeSudionici = (event: React.SyntheticEvent, value: PickerItem[], details?: string) => {
    setSelectedSudionici(value);
  };

  const handleChangeVodici = (event: React.SyntheticEvent, value: PickerItem[], details?: string) => {
    setSelectedVodici(value);
  };

  const handleInputChangeVodici = (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'selectOption' || reason === 'reset') return;
    setInputValueVodici(value);
  };

  const handleInputChangeSudionici = (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'selectOption' || reason === 'reset') return;
    setInputValueSudionici(value);
  };

  return (
    <Grid container spacing={2}>
      <Grid container size={{ xs: 12, md: 6 }} height={'fit-content'} spacing={2}>
        {allowCRUD ? (
          <Grid size={12}>
            <InfoBox labelToken='messages.izlet_vodici' />
          </Grid>
        ) : null}
        <Grid size={12} sx={{ display: 'flex', alignItems: 'center' }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            inputValue={inputValueVodici}
            onInputChange={handleInputChangeVodici}
            options={hpsUsersPickerRecords.filter(
              (opt) => !sudioniciRecords.some((rec) => rec.user_id === opt.value) && !vodiciRecords.some((rec) => rec.user_id === opt.value)
            )}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            value={selectedVodici}
            onChange={handleChangeVodici}
            disabled={!allowCRUD}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(titleVodici)}
              //placeholder={t(titleVodici)}
              />
            )}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {option.label}
                </li>
              );
            }}
            limitTags={2}
            sx={{ m: 1, width: 400 }}
          />
          <TableHeaderButton
            sx={{ height: 'fit-content', alignItems: 'center' }}
            onClick={() =>
              handleAdd(
                10,
                selectedVodici.map((item) => item.value as number)
              )
            }
            startIcon={<AddIcon />}
            variant="contained"
            disabled={!allowCRUD || !selectedVodici.length}
          >
            {t(addLabel)}
          </TableHeaderButton>
        </Grid>
        <Grid size={12}>
          <ModelTable
            title={t(titleVodici)}
            records={vodiciRecords}
            dc={dc}
            viewName={'default'}
            allowSelection="many"
            allowFilter={true}
            allowExport={false}
            allowAdd={false}
            allowRowAction={() => {
              return true;
            }}
            onRowAction={() => { }}
          >
            {allowCRUD ? <TableButtonDelete variant="outlined" onClick={(evt, sel) => handleDelete(evt, sel, 10)} /> : null}
          </ModelTable>
        </Grid>
      </Grid>
      <Grid container size={{ xs: 12, md: 6 }} height={'fit-content'} spacing={2}>
        {allowCRUD ? (
          <Grid size={12}>
            <InfoBox labelToken='messages.izlet_sudionici' />
          </Grid>
        ) : null}
        <Grid size={12} sx={{ display: 'flex', alignItems: 'center' }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            inputValue={inputValueSudionici}
            onInputChange={handleInputChangeSudionici}
            options={hpsUsersPickerRecords.filter(
              (opt) => !sudioniciRecords.some((rec) => rec.user_id === opt.value) && !vodiciRecords.some((rec) => rec.user_id === opt.value)
            )}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            value={selectedSudionici}
            onChange={handleChangeSudionici}
            disabled={!allowCRUD}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(titleSudionici)}
              //placeholder={t(titleSudionici)}
              />
            )}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {option.label}
                </li>
              );
            }}
            limitTags={2}
            sx={{ m: 1, width: 400 }}
          />
          <TableHeaderButton
            sx={{ height: 'fit-content' }}
            onClick={() =>
              handleAdd(
                20,
                selectedSudionici.map((item) => item.value as number)
              )
            }
            startIcon={<AddIcon />}
            variant="contained"
            disabled={!allowCRUD || !selectedSudionici.length}
          >
            {t(addLabel)}
          </TableHeaderButton>
        </Grid>
        <Grid size={12}>
          <ModelTable
            title={t(titleSudionici)}
            records={sudioniciRecords}
            dc={dc}
            viewName={'default'}
            allowSelection="many"
            allowFilter={true}
            allowExport={false}
            allowAdd={false}
            allowRowAction={() => {
              return true;
            }}
            onRowAction={() => { }}
          >
            {allowCRUD ? <TableButtonDelete variant="outlined" onClick={(evt, sel) => handleDelete(evt, sel, 20)} /> : null}
          </ModelTable>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default IzletSudioniciPane;
